import { useState } from "react";
import topApi from "../../components/api/ToPApi";
import Message from "../../components/ui/Message";

// Token de integración ecommerce (Mi empresa): el comercio lo genera y lo ve una sola vez. Al copiarlo desaparece
// de la pantalla; el API no lo guarda, así que si se pierde se genera otro. Generar uno revoca el anterior.
export const EcommerceTokenSettings = () => {
    const [token, setToken] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleGenerate = async () => {
        setConfirming(false);
        setIsGenerating(true);
        setCopied(false);
        try {
            const resp = await topApi.post<{ token: string }>('/security/merchantTokenEcom', {});
            setToken(resp.data.token);
        } catch (e: any) {
            setErrorMessage(e?.response?.data?.error || 'Ocurrió un error de comunicación, intente de nuevo.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(token);
            setToken('');
            setCopied(true);
        } catch (e) {
            setErrorMessage('No se pudo copiar el token. Selecciónelo y cópielo manualmente antes de salir de esta pantalla.');
        }
    };

    return (
        <div className="card border-0 shadow-sm">
            <div className="card-body">
                <h6 className="mb-1">Token de ecommerce</h6>
                <p className="text-muted small mb-3">
                    Genere el token para conectar su tienda en línea o plataforma de ecommerce con Swiftpay.
                </p>

                {token ? (
                    <div className="border border-warning rounded p-3 mb-3 bg-warning-subtle">
                        <div className="fw-medium mb-1"><i className="bi bi-exclamation-triangle me-1" />Copie el token ahora</div>
                        <div className="small mb-2">Solo se muestra esta vez. Al copiarlo desaparece de la pantalla y no se puede volver a consultar.</div>
                        <textarea
                            readOnly
                            aria-label="Token de ecommerce"
                            className="form-control mb-2"
                            style={styles.token}
                            rows={4}
                            value={token}
                            onFocus={(e) => e.target.select()}
                        />
                        <div className="d-flex justify-content-end">
                            <button type="button" className="btn btn-primary" onClick={handleCopy}>
                                <i className="bi bi-clipboard me-1" />Copiar token
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="border rounded p-3 mb-3">
                        {copied ? (
                            <div className="text-success">
                                <i className="bi bi-check-circle me-1" />Token copiado al portapapeles. Péguelo en su plataforma de ecommerce: el token anterior ya no funciona.
                            </div>
                        ) : (
                            <ul className="small text-muted mb-0 ps-3">
                                <li>El token se muestra una sola vez; al copiarlo desaparece.</li>
                                <li>Solo el último token generado funciona: al generar uno nuevo, el anterior deja de servir.</li>
                                <li>Si lo pierde, genere uno nuevo y actualícelo en su plataforma.</li>
                                <li>No lo comparta: permite cobrar a nombre de su empresa.</li>
                            </ul>
                        )}
                    </div>
                )}

                {!token && (
                    <div className="d-flex justify-content-end align-items-center gap-2">
                        {confirming ? (
                            <>
                                <span className="small text-danger me-auto">
                                    <i className="bi bi-exclamation-circle me-1" />El token actual dejará de funcionar y deberá actualizarlo en su plataforma. ¿Continuar?
                                </span>
                                <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirming(false)}>Cancelar</button>
                                <button type="button" className="btn btn-danger" onClick={handleGenerate}>Sí, generar</button>
                            </>
                        ) : (
                            <button type="button" className="btn btn-primary" onClick={() => setConfirming(true)} disabled={isGenerating}>
                                {isGenerating
                                    ? <><span className="spinner-border spinner-border-sm me-1" /> Generando...</>
                                    : <><i className="bi bi-key me-1" /> Generar token</>}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {errorMessage &&
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />}
        </div>
    );
};

const styles = {
    token: {
        fontFamily: 'monospace',
        fontSize: 13,
        wordBreak: 'break-all' as const,
        resize: 'none' as const,
        backgroundColor: '#fff',
    },
};
